import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface Promotion {
  promotionId: string;
  providerId?: string;
  title: string;
  description: string;
  promotionType: 'commission_discount' | 'bonus_payout' | 'fixed_bonus';
  discountPercentage?: number;
  bonusAmount?: number;
  minBookingAmount?: number;
  zipCodes?: string[];
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PromotionPerformance {
  status: 'upcoming' | 'active' | 'expired';
  affectedProvidersCount: number;
  estimatedBenefit: number;
  estimatedBookings: number;
  startDate: string;
  endDate: string;
}

export interface PromotionListResponse {
  promotions: Promotion[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListPromotionsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  providerId?: string;
  promotionType?: string;
  isActive?: boolean;
}

class PromotionManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listPromotions(params?: ListPromotionsParams): Promise<PromotionListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.promotionType) queryParams.append('promotionType', params.promotionType);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const response = await this.api.get<{ data: PromotionListResponse }>(
      `/admin/promotions?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async createPromotion(promotion: Omit<Promotion, 'promotionId' | 'createdAt' | 'updatedAt'>): Promise<Promotion> {
    const response = await this.api.post<{ data: { promotion: Promotion } }>('/admin/promotions', promotion);
    return response.data.data.promotion;
  }

  async updatePromotion(promotionId: string, updates: Partial<Promotion>): Promise<Promotion> {
    const response = await this.api.put<{ data: { promotion: Promotion } }>(
      `/admin/promotions/${promotionId}`,
      updates
    );
    return response.data.data.promotion;
  }

  async deletePromotion(promotionId: string): Promise<Promotion> {
    const response = await this.api.delete<{ data: { promotion: Promotion } }>(
      `/admin/promotions/${promotionId}`
    );
    return response.data.data.promotion;
  }

  async getPromotionPerformance(promotionId: string): Promise<{ promotion: Promotion; performance: PromotionPerformance }> {
    const response = await this.api.get<{ data: { promotion: Promotion; performance: PromotionPerformance } }>(
      `/admin/promotions/${promotionId}/performance`
    );
    return response.data.data;
  }
}

export const promotionManagementService = new PromotionManagementService();
